import { NextPage } from 'next';
import { useRouter } from 'next/router';
import {
  Breadcrumb,
  GlobalErrorFallback,
  ProductCarousel,
  ProductDetails,
} from 'components';
import { BaseContainer } from 'styles/sharedStyles';
import {
  useProductDetails,
  usePromotionList,
  useSubscriptionHistory,
  useFetchProductStrategy,
} from 'reactQuery/queries';
import { LoadingBar } from 'components/core';
import { buildCarouselPlaceholderData } from 'components/ProductCarousel';
import Head from 'next/head';
import { useI18n } from 'contexts/i18n';
import { getInitialProps } from 'utils/nextServerFuncitons';
import { ProductNotFound } from 'components/ProductDetails/ProductNotFound';
import { useMemo } from 'react';

const ProductDetailsPage: NextPage = () => {
  const i18n = useI18n();
  const router = useRouter();
  const productSlug = router.query.slug as string;

  const {
    data: productDetails,
    isLoading,
    isIdle,
    isError,
    error,
  } = useProductDetails(productSlug);

  const productTaxonomySlug =
    productDetails?.breadcrumb[productDetails?.breadcrumb.length - 1]
      ?.permalink ?? '';

  const {
    data: smProducts,
    isLoading: isSimilarProductsLoading,
    isError: isSimilarProductsError,
  } = useFetchProductStrategy({
    taxonslug: productTaxonomySlug,
    variantIds: [],
    skus: [],
  });

  const { data: doctorPromotionList, isLoading: isPromotionListLoading } =
    usePromotionList();

  const subscriptionHistory = useSubscriptionHistory();

  const similarProducts =
    smProducts?.pages?.[0]?.products ?? buildCarouselPlaceholderData();

  const STORE_HOME_LEVEL = -1;

  const breadcrumbItems = useMemo(() => {
    const storeHome = {
      id: 0,
      level: STORE_HOME_LEVEL,
      name: 'Store Home',
      permalink: '/',
    };
    if (!productDetails) return [storeHome];
    // Strip any pre-existing Store Home entries (level === -1) added by the service layer to avoid duplicates.
    // Do NOT pre-prefix with /category/ — Breadcrumb.tsx is the single authority for that.
    const crumbs = productDetails.breadcrumb.filter(
      c => c.level !== STORE_HOME_LEVEL
    );
    return [storeHome, ...crumbs];
  }, [productDetails]);

  if (
    subscriptionHistory.isLoading ||
    isPromotionListLoading ||
    isLoading ||
    isIdle
  ) {
    return <LoadingBar />;
  }
  if (isError) {
    if (error?.response?.status === 404) {
      router.push('/');
      return <></>;
    } else {
      return <GlobalErrorFallback />;
    }
  }

  if (productDetails.id === '') {
    return <ProductNotFound />;
  }

  return (
    <>
      <Head>
        <title>{productDetails?.name}</title>
      </Head>
      <BaseContainer>
        <Breadcrumb items={breadcrumbItems} />
        <ProductDetails
          key={productSlug}
          product={productDetails}
          doctorPromotionList={doctorPromotionList}
          subscriptionHistory={subscriptionHistory}
        />
        {productTaxonomySlug && (
          <ProductCarousel
            title={i18n['You might also like']}
            products={similarProducts}
            isError={isSimilarProductsError}
            isLoading={isSimilarProductsLoading}
            doctorPromotionList={doctorPromotionList}
          />
        )}
      </BaseContainer>
    </>
  );
};

ProductDetailsPage.getInitialProps = getInitialProps;

export default ProductDetailsPage;
